import { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchProperties } from '../services/propertyService.js'
import { extractOutcode, lookupCoordinates } from '../services/postcodeService.js'
import { PropertyDataContext } from './PropertyDataContext.js'

const PUBLIC_STATUSES = new Set(['available', 'under_offer', 'under offer', 'sold_stc', 'sold stc', 'sold'])
const PROPERTY_REFRESH_INTERVAL_MS = 2 * 60 * 1000

const normalizeStatus = (value = '') => value.toLowerCase().replace(/\s+/g, '_')

const filterPublicProperties = (list = []) => list.filter((property) => PUBLIC_STATUSES.has(normalizeStatus(property.status)))

const isFiniteCoordinate = (value) => Number.isFinite(Number(value))

const enrichPropertiesWithGeo = async (list = []) => {
  const outcodeAssignments = new Map()
  const lookups = new Map()

  list.forEach((property) => {
    const hasCoords = isFiniteCoordinate(property.latitude) && isFiniteCoordinate(property.longitude)
    const derivedOutcode = property.postcode || extractOutcode(property.location || property.city || '')
    if (derivedOutcode) {
      outcodeAssignments.set(property.id, derivedOutcode)
      if (!hasCoords && !lookups.has(derivedOutcode)) {
        lookups.set(derivedOutcode, null)
      }
    }
  })

  await Promise.all(
    [...lookups.keys()].map(async (outcode) => {
      try {
        const coords = await lookupCoordinates(outcode)
        lookups.set(outcode, coords)
      } catch {
        lookups.set(outcode, null)
      }
    }),
  )

  return list.map((property) => {
    const outcode = outcodeAssignments.get(property.id)
    const coords = outcode ? lookups.get(outcode) : null
    const hasCoords = isFiniteCoordinate(property.latitude) && isFiniteCoordinate(property.longitude)
    const needsPostcode = outcode && !property.postcode
    const needsCoords = !hasCoords && coords
    if (!needsPostcode && !needsCoords) {
      return property
    }
    return {
      ...property,
      postcode: property.postcode || outcode || null,
      latitude: hasCoords ? property.latitude : coords?.latitude ?? null,
      longitude: hasCoords ? property.longitude : coords?.longitude ?? null,
    }
  })
}

export const PropertyDataProvider = ({ children }) => {
  const [allProperties, setAllProperties] = useState([])
  const [properties, setProperties] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async (options = {}) => {
    const { background = false } = options
    try {
      if (!background) {
        setLoading(true)
      }
      const data = await fetchProperties()
      let enriched = data
      try {
        enriched = await enrichPropertiesWithGeo(data)
      } catch (geoError) {
        console.warn('Unable to enrich property coordinates', geoError)
      }
      setAllProperties(enriched)
      setProperties(filterPublicProperties(enriched))
      setError(null)
    } catch (err) {
      console.error('Unable to load properties', err)
      setError(err)
    } finally {
      if (!background) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const intervalId = setInterval(() => {
      load({ background: true })
    }, PROPERTY_REFRESH_INTERVAL_MS)
    return () => {
      clearInterval(intervalId)
    }
  }, [load])

  const value = useMemo(
    () => ({
      properties,
      allProperties,
      loading,
      error,
      refresh: load,
    }),
    [properties, allProperties, loading, error, load],
  )

  return <PropertyDataContext.Provider value={value}>{children}</PropertyDataContext.Provider>
}
